import { HttpResponse } from '@angular/common/http';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';

import { EidosConfigService } from '@app/core/config/eidos-config.service';
import { EidosIconType } from '@app/core/models/core-constant.model';
import { DynamicApiResponse, IDynamicApiErrorResponse, IDynamicApiResponse } from '@app/core/models/dynamic-api-response.model';
import { CoreCacheService } from '@app/core/services/core-cache.service';
import { CoreFormatService } from '@app/core/services/core-format.service';
import { EidosApiService } from '@app/core/services/eidos-api.service';
import { CoreUtilityDialog } from '@app/core/services/eidos-utility.service';
import { SpinnerOverlayService } from '@app/core/services/spinner-overlay.service';
import { ReservationEditSupplementAction } from '../components/res-bkg-edit-supplement/res-bkg-edit-supplement.component';
import {
  BookingSectionResCard,
  IResCardLineOptions,
  IResCardLineOptionsActions,
  IResCardLineOptionsHyperlink,
  ResCard,
  ResCardLine,
  ResCardLineOptionType,
} from '../components/res-card/res-card.model';
import { ReservationApiService } from '../services/reservation-api.service';
import { ReservationService } from '../services/reservation.service';
import { ReservationBookingLock } from './res-booking-lock.model';
import {
  BookingGuest,
  BookingJourneySupplement,
  BookingSection,
  BookingSupplement,
  IReservationApiBooking,
  ResBkgLinks,
  ResBooking,
  ResBookingFinancialRecap,
  ResBookingGifFlag,
} from './res-booking.model';
import { IReservationCompany } from './res-cached-data.models';
import { ResInsuranceAction } from './res-insurance.model ';
import {
  ReservationAction,
  ReservationActionBooking,
  ReservationEditActionBooking,
} from '../../res-common/models/reservation-permission.model';
import { RescomService } from '@app/res-common/services/rescom.service';
import { IResBookingActionCommand } from '@app/res-common/models/rescom.model';
import { ResBkgItem } from './res-bkg-item.model';
import { CoreModalService, ModalBaseActions } from '@app/core/services/core-modal.service';
import { CoreGridModalColumn, CoreGridModalComponent } from '@app/core/components/core-modals/core-grid-modal/core-grid-modal.component';
import { ResPaymentMilestoneComponent } from '../components/res-payment-milestone/res-payment-milestone.component';
import { getRootInjector } from '@app/core/injector/root-injector';
import { EidosSecurityService } from '@app/core/services/eidos-security.service';

export type ReservationToBeCheckedActionBooking = 'ChangeVoyage' | '';


export type ResRequestSupplementActionData =  {supplement:BookingJourneySupplement|BookingSupplement,action:number}

export class ResBookingUtility {
  static readonly ACTION_ADDOPTION_PRE = 1;
  static readonly ACTION_ADDOPTION_POST = 2;

  requestSupplementAction = new Subject<ResRequestSupplementActionData>()
  addOptionFilters = new BehaviorSubject<any>({})


  /**
   * Reservation actions in booking to be check by API
   *
   * @private
   * @static
   * @memberof ResBookingUtility
   */
  private static readonly RESERVATION_ACTION_BOOKING_TBCHECKED = [
    ReservationActionBooking.ChangeVoyageBookingVersion,
  ];
  /**
   * Is anon access, the bkg:
   * - is retrieved by GUID probably in checkout page
   *
   * @memberof ResBookingUtility
   */
  public bookingRetrievedInAnonymous = false;
  /**
   * Is read only access
   * - User has read only role
   *
   * @memberof ResBookingUtility
   */
  private _bookingRetrievedInReadOnly = false;
  set bookingRetrievedInReadOnly(val: boolean) {
    this._bookingRetrievedInReadOnly = val;
    this.bookingLock.bookingRetrievedInReadOnly = val;
  }
  get bookingRetrievedInReadOnly(): boolean {
    return this._bookingRetrievedInReadOnly;
  }
  /**
   * Current booking
   *
   * @type {BehaviorSubject<ResBooking>}
   * @memberof ReservationService
   */
  public current: BehaviorSubject<ResBooking> = new BehaviorSubject<ResBooking>(
    new ResBooking()
  );
  public lastSidebarCards: BehaviorSubject<BookingSectionResCard[]> = new BehaviorSubject<BookingSectionResCard[]>([]);
  /**
   * Current selected section ID
   *
   * @type {BehaviorSubject<number>}
   * @memberof ResBookingUtility
   */
  public selectedSectionId: BehaviorSubject<number> =
    new BehaviorSubject<number>(1);
  /**
   * Current selected section
   *
   * @readonly
   * @type {BookingSection}
   * @memberof ResBookingUtility
   */
  public get selectedSection(): BookingSection {
    return this.current.value.getSection(this.selectedSectionId.value);
  }
  /**
   * Current booking Lock
   *
   * @type {ReservationBookingLock}
   * @memberof ResBookingUtility
   */
  public bookingLock: ReservationBookingLock;
  /**
   * Reservation API service reference
   *
   * @private
   * @type {ReservationApiService}
   * @memberof ResBookingUtility
   */
  private reservationApiService: ReservationApiService;
  /**
   * Core API service reference
   *
   * @private
   * @type {EidosApiService}
   * @memberof ResBookingUtility
   */
  private coreApiService: EidosApiService;

  /**
   * Booking action command subject
   *
   * @type {Subject<IResBookingActionCommand>}
   * @memberof ResBookingUtility
   */
  actionCommand$: Subject<IResBookingActionCommand> =
    new Subject<IResBookingActionCommand>();
  /**
   * Previous booking action checks dict
   *
   * @type {{ [key in ReservationToBeCheckedActionBooking]?: IDynamicApiErrorResponse[] }}
   * @memberof ResBookingUtility
   */
  canDoActionInBooking: {
    [key in ReservationToBeCheckedActionBooking]?: IDynamicApiErrorResponse[];
  } = {};

  openInsuranceCommand$: Subject<{
    guest?: BookingGuest;
    action?: ResInsuranceAction;
  }> = new Subject<{ guest?: BookingGuest; action?: ResInsuranceAction }>();

  editSupplementsCommand$ = new Subject<{
    supplement: BookingSupplement | BookingJourneySupplement;
    action: ReservationEditSupplementAction;
  }>();

  ecmDataChanged$: Subject<object> = new Subject<{}>();

  linkUpdated = new BehaviorSubject<ResBkgLinks>(new ResBkgLinks())

  private coreCacheService: CoreCacheService
  private rescomService: RescomService
  initialCommissionValue?: number
  coreModalService: CoreModalService
  private spinnerOverlayService: SpinnerOverlayService

  constructor(
    public dialogs: CoreUtilityDialog,
    public resService: ReservationService,
    reservationApiService: ReservationApiService,
    coreApiService: EidosApiService,
    coreCacheService: CoreCacheService,
    rescomService: RescomService,
    coreModalService: CoreModalService,
    spinnerOverlayService: SpinnerOverlayService,
  ) {
    this.reservationApiService = reservationApiService;
    this.coreApiService = coreApiService;
    this.coreCacheService = coreCacheService;
    this.rescomService = rescomService;
    this.coreModalService = coreModalService;
    this.spinnerOverlayService = spinnerOverlayService;

    this.bookingLock = new ReservationBookingLock(
      this.reservationApiService,
      dialogs,
      resService
    );

    // Reset action checks
    this.current.subscribe(() => (this.canDoActionInBooking = {}));
  }

  /**
   * Has current booking flag getter
   *
   * @readonly
   * @type {boolean}
   * @memberof ResBookingUtility
   */
  public get hasCurrentBooking(): boolean {
    return this.current.getValue().isValid();
  }
  /**
   * Load booking by GUID
   * Does not require auth
   *
   * @param {string} guid
   * @return {*}  {Promise<boolean>}
   * @memberof ResBookingUtility
   */
  public loadBkgByGuid(guid: string): Promise<boolean> {
    this.bookingRetrievedInAnonymous = true;
    this.bookingLock.bookingRetrievedInAnonymous = true;

    return new Promise<boolean>((resolve) => {
      this.coreApiService
        .getPublicAPI('v1/booking/SelectByGuid', 'POST', { Guid: guid })
        .pipe(
          map<HttpResponse<DynamicApiResponse>, DynamicApiResponse>(
            (response) => new DynamicApiResponse(response)
          )
        )
        .subscribe({
          next: (data: DynamicApiResponse) => {
            const booking = new ResBooking(data.body as IReservationApiBooking);
            this.current.next(booking);
            resolve(true);
          },
          error: () => {
            resolve(false);
          },
          complete: () => {
            resolve(true);
          },
        });
    });
  }
  /**
   * Load booking by ID
   * Requires auth
   *
   * @param {number} [bkgId]
   * @return {*}  {Promise<boolean>}
   * @memberof ResBookingUtility
   */
  public loadBkg(bkgId?: number): Promise<boolean> {
    this.spinnerOverlayService.show();

    const _bkgId = bkgId ?? this.current.getValue().bkgID;
    if (!_bkgId || _bkgId === ResBooking.INVALID_BOOKING) {
      this.spinnerOverlayService.hide();
      return new Promise<boolean>((resolve) => resolve(false))
    }

    const isBookingChanged = this.current.getValue().bkgID !== _bkgId;
    if(isBookingChanged) {
      this.sectionCurrentView.next(undefined)
    }

    //read promo booking
    this.resService.promoUtility.getBookingPromo(_bkgId);

    return new Promise<boolean>((resolve) => {

      combineLatest([
        this.reservationApiService.getBooking({ BkgID: _bkgId }),
        this.coreCacheService.getCachedData<IReservationCompany>('Companies'),
        this.rescomService.getAvailableCompanies()
      ])
      .subscribe({
        next: ([bkg,companies]) => {

          if (this.bookingRetrievedInAnonymous) {
            this.current.next(bkg);
            resolve(true)
            return
          }

          if (bkg.isValid() && bkg.jonesActAlert) {
            this.resService.warningJonesAct()
          }

          this.resService.updateEnvWithBooking(bkg).then(_=>{
            let isCurrentlyLocked = this.bookingLock.isLocked(_bkgId);
            if (!isCurrentlyLocked) {
              this.bookingLock.lock(_bkgId);
            }
            this.current.next(bkg);
            //@TODO CHECK USER HAS VISIBILITY ON THIS COMPANY
            //this.coreUtilityService.getAvailableCompanies()
            const curCompany = this.rescomService.currentCompany.getValue()
            if(!this.resService.checkCompanyAlias(curCompany?.CompanyID ?? 0,bkg.companyID)) {
              let company = companies.find(c=>c.CompanyID===bkg.companyID)
              if(!company) this.resService.allCompanies.find(c => this.resService.checkCompanyAlias(c.CompanyID,bkg.companyID))
              if(!bkg.keepCompany) this.resService.setCompany(company)
            }
          });

          // if (this.initialCommissionValue && this.initialCommissionValue !== bkg.financialRecap[0]?.commission && bkg.financialRecap[0]?.commissionPaid! > 0){
          //   this.rescomService.utilityDialogs.alert({
          //     message: 'The Commission value has been updated.',
          //     title: `Attention`,
          //   }).then(_ => {})
          // }
          // this.initialCommissionValue = bkg.financialRecap[0]?.commission ?? 0
          const currentView = this.sectionCurrentView.getValue()
          this.sectionCurrentView.next(currentView ? currentView : 0)
          resolve(true)
        },
        error: () => {
          this.spinnerOverlayService.hide();
          resolve(false);
        },
        complete: () => {
          this.spinnerOverlayService.hide();
          resolve(true);
            // const bkg = this.current.getValue()
            // if(bkg.isValid() && bkg.jonesActAlert) {
            //   this.resService.warningJonesAct()
            // }
        },
      });
    });
  }

  /**
   * Reload current booking
   * Requires auth
   *
   * @return {*}
   * @memberof ResBookingUtility
   */
  public reloadCurrentBkg() {
    return this.loadBkg();
  }
  public reorderSupplement() {
    const booking = this.current.getValue()
    booking.sortSupplement()
  }
  public sectionCurrentView = new BehaviorSubject<number | undefined>(undefined)
  public dayByDayTabToSelect = new BehaviorSubject<number | undefined>(undefined)

  /**
   * Reload current booking financials
   * Requires auth
   *
   * @return {*}
   * @memberof ResBookingUtility
   */
  public reloadCurrentBkgFinancials() {
    const bkg = this.current.getValue();

    return new Promise<boolean>((resolve) => {
      if (bkg.isValid()) {
        this.reservationApiService.getBookingFinancials(bkg.bkgID).subscribe({
          next: (financials: Array<ResBookingFinancialRecap>) => {
            bkg.financialRecap = financials;
            this.current.next(bkg);
          },
          error: () => {
            resolve(false);
          },
          complete: () => {
            resolve(true);
          },
        });
      } else {
        resolve(false);
      }
    });
  }
  /**
   * Checks if current booking is R/O
   *
   * @param {ResBooking} [bkg]
   * @return {*}  {boolean}
   * @memberof ResBookingUtility
   */
  public isCurrentBkgReadOnly(bkg?: ResBooking): boolean {
    if (!bkg) {
      bkg = this.current.getValue();
    }

    if (bkg.isDeleted()) return true;

    return false;
  }
  /**
   * Checks if currently locked booking has bkgID parameter as ID and it R/O
   *
   * @param {number} bkgID
   * @return {*}  {boolean}
   * @memberof ResBookingUtility
   */
  public isCurrentBkgNetFare(bkg?: ResBooking): boolean {
    return bkg?.isNetFare ?? this.current.getValue().isNetFare;
  }

  public isCurrentPriceType(priceTypeID: number, bkg?: ResBooking): boolean {
    if (!bkg) {
      bkg = this.current.getValue();
    }
    return bkg.priceTypeID === priceTypeID;
  }
  /**
   * Checks if current booking is locked
   *
   * @param {ResBooking} [bkg]
   * @return {*}  {boolean}
   * @memberof ResBookingUtility
   */
  public isCurrentBkgLocked(bkg?: ResBooking): boolean {
    if (!bkg) {
      bkg = this.current.getValue();
    }
    return bkg.isValid() && this.bookingLock.isLocked(bkg.bkgID);
  }
  /**
   * Checks if currently locked booking has bkgID parameter as ID and it R/O
   *
   * @param {number} bkgID
   * @return {*}  {boolean}
   * @memberof ResBookingUtility
   */
  public isBkgReadOnlyByID(bkgID: number): boolean {
    return (
      bkgID !== ResBooking.INVALID_BOOKING && !this.bookingLock.isLocked(bkgID)
    );
  }
  /**
   * Checks if currently locked booking has bkgID parameter as ID
   *
   * @param {number} bkgID
   * @return {*}  {boolean}
   * @memberof ResBookingUtility
   */
  public isBkgLockedByID(bkgID: number): boolean {
    return (
      bkgID !== ResBooking.INVALID_BOOKING && this.bookingLock.isLocked(bkgID)
    );
  }

  public hasCurrentBkgBonusCommission25(bkg?: ResBooking): boolean {
    if (!bkg) {
      bkg = this.current.getValue();
    }
    return bkg.bC25;
  }
  /**
   * Checks if can perform an action in current booking
   *
   * @param {ReservationActionBooking} action
   * @param {number} [sectionID]
   * @param {number} [bkgID]
   * @return {*}  {Promise<boolean>}
   * @memberof ResBookingUtility
   */
  checkBkgAction(
    action: ReservationActionBooking,
    sectionID?: number,
    bkgID?: number
  ): Promise<IDynamicApiErrorResponse[]> {
    return new Promise<IDynamicApiErrorResponse[]>((resolve) => {
      if (!bkgID) {
        bkgID = this.current.getValue().bkgID;
      }

      if (bkgID !== ResBooking.INVALID_BOOKING) {
        let mappedAction: ReservationToBeCheckedActionBooking = '';

        switch (action) {
          case ReservationActionBooking.ChangeVoyageBookingVersion:
            mappedAction = 'ChangeVoyage';
            break;
          default:
            break;
        }

        if (
          !!mappedAction &&
          mappedAction in this.canDoActionInBooking
        ) {
          resolve(this.canDoActionInBooking[mappedAction]!);
        } else {
          const spinnerOverlayService = this.spinnerOverlayService;

          spinnerOverlayService.show();

          this.reservationApiService
            .checkBookingAction({
              BkgID: bkgID,
              Action: action,
              VersionID: sectionID,
            })
            .subscribe({
              next: () => {
                spinnerOverlayService.hide();

                this.canDoActionInBooking[mappedAction] = [];
                resolve([]);
              },
              error: (error: IDynamicApiResponse) => {
                spinnerOverlayService.hide();

                this.canDoActionInBooking[mappedAction] = error.Errors;
                this.rescomService.utilityDialogs.error({
                  message: '',
                  error: error.Errors.map((e: any) => e.ErrorDesc),
                  title: `ERROR IN BOOKING ACTION`,
                  size: '380px',
                });
                resolve(error.Errors);
              },
            });
        }
      } else {
        this.rescomService.utilityDialogs.error({
          message: '',
          error: ['No current booking selected'],
          title: `ERROR IN BOOKING ACTION`,
          size: '380px',
        });
        resolve([
          {
            ErrorCod: '',
            ErrorDesc: 'No current booking',
          },
        ]);
      }
    });
  }
  /**
   * Eventually checks if can perform an action in current booking than do it
   *
   * @param {ReservationActionBooking} action
   * @param {number} [sectionID]
   * @param {number} [bkgID]
   * @return {*}  {Promise<IDynamicApiErrorResponse[]>}
   * @memberof ResBookingUtility
   */
  launchBkgAction(
    action: ReservationActionBooking,
    sectionID?: number,
    bkgID?: number,
    ...actionParams: any[]
  ): Promise<IDynamicApiErrorResponse[]> {
    return new Promise<IDynamicApiErrorResponse[]>(async (resolve) => {
      if (!bkgID) {
        bkgID = this.current.getValue().bkgID;
      }

      if (
        ResBookingUtility.RESERVATION_ACTION_BOOKING_TBCHECKED.includes(action)
      ) {
        const checkBkgAction = await this.checkBkgAction(
          action,
          sectionID,
          bkgID
        );
        if (checkBkgAction.length > 0) {
          resolve(checkBkgAction);
          return;
        }
      }

      this.actionCommand$.next({
        action: action,
        sectionID: sectionID,
        actionParams: actionParams,
      });

      resolve([]);
    });
  }
  /**
   * Leave current booking handler
   * Includes
   * - Booking unlocking
   * - Lock hiding
   * - Current booking reset
   *
   * @memberof ResBookingUtility
   */
  public leaveCurrentBooking() {
    this.bookingLock.hide();
    this.current.next(new ResBooking());
  }

  viewFinacialDetails(line:any) {
    let section = 'Paid'

    if(line.caption.toLowerCase().includes('charge')) {
      section = 'Charge'
    }

    const bkg = this.current.getValue();
    const columns: CoreGridModalColumn[] = [
      { alignment: 'center', dataField: 'payer', caption: 'Payer' },
      { alignment: 'center', dataField: 'totalCharge', caption: 'Total Charge', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'netCommission', caption: 'Net Commission', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'vatCommission', caption: 'VAT Commission', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'totalCommission', caption: 'Total Commission', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'totalPaid', caption: 'Total Paid', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'totalDue', caption: 'Total Due', cellTemplate: 'currencyTemplate' },
    ];

    this.coreModalService
      .open(CoreGridModalComponent,
        {
          title: 'Financial Split ' + section + ' - ' + bkg.bkgID,
          data: {
            columns,
            loadData: () => this.reservationApiService.getFinancialSplit(bkg.bkgID, section).pipe(
              map((rows) => rows.map((row) => ({ ...row, bkgCurrency: bkg.bkgCurrency })))
            ),
            noDataText: 'No Financial Split found',
            currencyField: 'bkgCurrency',
            onLoadError: (err: unknown) => {
              const apiError = err as IDynamicApiResponse;

              this.rescomService.utilityDialogs.error({
                message: 'Error. Financial Split Not Loaded.',
                error: apiError?.Errors?.map((e: any) => e.ErrorDesc),
                title: 'ERROR - Financial Split',
                size: '380px',
              })
            },
            pagerVisible: false,
          },
          dimensions: {
            width: '1200px',
            height: '50vh',
            maxWidth: '95vw',
            maxHeight: '95vh',
          },
          internalScroll: true,
        })
  }

  viewFinacialDetailsMultiBkg() {
    const bkgID = this.current.getValue().bkgID;
    const columns: CoreGridModalColumn[] = [
      { alignment: 'center', dataField: 'currency', caption: 'Currency' },
      { alignment: 'center', dataField: 'totalCharge', caption: 'Total Charge', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'totalPaid', caption: 'Total Paid', cellTemplate: 'currencyTemplate' },
      { alignment: 'center', dataField: 'totalDue', caption: 'Total Due', cellTemplate: 'currencyTemplate' },
    ];

    this.coreModalService
      .open(CoreGridModalComponent,
        {
          title: 'Financial Split Multicurrency Booking' + ' - ' + bkgID,
          data: {
            columns,
            loadData: () => this.reservationApiService.getFinancialSplitMultiCurrency(bkgID),
            noDataText: 'No Financial Split Multicurrency found',
            currencyField: 'currency',
            onLoadError: (err: unknown) => {
              const apiError = err as IDynamicApiResponse;

              this.rescomService.utilityDialogs.error({
                message: 'Error. Financial Split Multicurrency Not Loaded.',
                error: apiError?.Errors?.map((e: any) => e.ErrorDesc),
                title: 'ERROR - Financial Split Multicurrency',
                size: '380px',
              })
            },
            pagerVisible: false,
          },
          dimensions: {
            width: '900px',
            height: '50vh',
            maxWidth: '95vw',
            maxHeight: '95vh',
          },
          internalScroll: true,
        })
  }

  viewPaymentMilestones() {
    const bkg = this.current.getValue();
    this.coreModalService.open(ResPaymentMilestoneComponent, {
      title: 'View / Edit Payment Milestones - Booking ID: ' + bkg.bkgID,
      data: bkg.bkgID,
      dataBinding: ['bkgID'],
      hasActions: ModalBaseActions.NONE,
      dimensions: {
        width: '95vw',
        height: '90vh',
      },
      internalScroll: true,
    });
  }

  /**
   * Computes sidebar cards for a light booking
   *
   * @static
   * @param {ResBooking} b
   * @param {CoreFormatService} coreFormatService
   * @return {*}  {ResCard[]}
   * @memberof ResBookingUtility
   */
  public static loadCardsForLightBooking(
    b: ResBooking,
    coreFormatService: CoreFormatService,
    eidosConfigService: EidosConfigService,
    resService: ReservationService,
    rescomService: RescomService
  ): ResCard[] {
    const cards: ResCard[] = [];
    let card = new ResCard();

    const iconFinacialDetails = {
      iconType: EidosIconType.Awesome,
      iconCode: "fa-plus-circle",
      iconSize: 0.6,
    }
    const finacialActions:IResCardLineOptionsActions[] = [
      {
        icon: iconFinacialDetails,
        callback: resService.bookingUtility.viewFinacialDetails
      },
    ]

    if (!b.isOpenDeposit) {
      card.title = 'GENERAL INFO';

      card.lines.push(new ResCardLine('Status', b.bkgStatus ?? ''));
      // card.lines.push(new ResCardLine('Type', b.bkgType ?? ''));
      card.lines.push(new ResCardLine('Company', b.companyName ?? ''));
      if(b.canEditBrandName) card.lines.push(new ResCardLine('Brand', b.brandName ?? ''));
      card.lines.push(new ResCardLine('Price Type', b.priceTypeName ?? ''));
      card.lines.push(new ResCardLine('Currency', b.bkgCurrency ?? ''));

      const dateStartFormatted: string = !!b.travelStartDate
        ? b.travelStartDate.toFormat(coreFormatService.DateFmtWithMonthName())
        : '';
      const dateEndFormatted: string = !!b.travelEndDate
        ? b.travelEndDate.toFormat(coreFormatService.DateFmtWithMonthName())
        : '';

      card.lines.push(
        new ResCardLine('Date', `${dateStartFormatted} - ${dateEndFormatted}`)
      );

      b.sections.forEach((s) => {
        card.lines.push(
          new ResCardLine('Guest N.', s.sectionID.toString(), {
            status: s.sectionID.toString(),
          } as IResCardLineOptions)
        );
      });

      if (b.grpID ?? 0 > 0) {
        const hyperlink = {
          url: eidosConfigService.getLinks('groupmaster')?.template,
          target: '_blank',
          parameters: { id: b.grpID },
        } as IResCardLineOptionsHyperlink;
        card.lines.push(
          new ResCardLine(
            'Group',
            b.grpName.length > 0 ? `${b.grpName} - (${b.grpID})` : `${b.grpID}`,
            { hyperlink: hyperlink } as IResCardLineOptions
          )
        );
      }

      card.lines.push(new ResCardLine('Note', b.note));

      cards.push(card);
    }

    card = new ResCard();
    card.title = 'BOOKING OWNER';

    if (b.owner) {
      if(b.goodwill) {
        card.lines.push(
          new ResCardLine('Booking Name', b.bkgName)
        );

      }
      if (b.owner.isAgent()) {
        const hyperlink = {
          url: eidosConfigService.getLinks('entitymaster')?.template,
          target: '_blank',
          parameters: { entity: 'agency', id: b.owner.agencyID },
        } as IResCardLineOptionsHyperlink;

        card.lines.push(
          new ResCardLine('Agency', b.owner.displayName(), {
            hyperlink: hyperlink,
          } as IResCardLineOptions)
        );
        // Open point Carlo 10.3.2023
        // Add IATA and Agency to the booking owner screen
        if (b.owner.agencyIataCod) {
          card.lines.push(
            new ResCardLine('IATA Code', b.owner.agencyIataCod ?? '')
          );
        }
        if (b.owner.agencyGroup) {
          card.lines.push(
              new ResCardLine('Agency Group', b.owner.agencyGroup ?? '')
          );
        }

        card.lines.push(
          new ResCardLine('Contact', b.owner.displayContactName())
        );
      } else {
        card.lines.push(new ResCardLine('Direct', b.owner.displayName()));
      }

      let address = b.owner.addressToDisplay;
      if (b.owner.address2) {
        address += `${!!b.owner.addressToDisplay ? ' - ' : ''} ${!!b.owner
          .address2}`;
      }
      card.lines.push(new ResCardLine('Address', address ?? ''));
      card.lines.push(new ResCardLine('Country', b.owner.country ?? ''));
      card.lines.push(new ResCardLine('Email', b.owner.email ?? ''));
      card.lines.push(new ResCardLine('Phone', b.owner.phone ?? ''));

      if (b.isOpenDeposit && b.owner?.displayGuest) {
        card.lines.push(new ResCardLine('Guest', b.owner.guest ?? ''));
      }
      cards.push(card);
    }

    card = new ResCard();
    card.title = 'FINANCIAL RECAP';
    if (b.financialRecap) {
      card.lines.push(
        new ResCardLine(
          'Total Charge',
          coreFormatService.CurrencyAmount(
            !!b.financialRecap.length
              ? b.financialRecap[0].totalCharge ?? 0
              : 0,
            b?.bkgCurrency
          ),
          {
            optionRender: ResCardLineOptionType.AlignRight,
          } as IResCardLineOptions
        )
      );
      card.lines.push(
        new ResCardLine(
          'Total Paid',
          coreFormatService.CurrencyAmount(
            !!b.financialRecap.length
              ? b.financialRecap[0].totalPayment ?? 0
              : 0,
            b?.bkgCurrency
          ),
          {
            optionRender: ResCardLineOptionType.AlignRight,
          } as IResCardLineOptions
        )
      );
      card.lines.push(
        new ResCardLine(
          'Total Due',
          coreFormatService.CurrencyAmount(
            !!b.financialRecap.length ? b.financialRecap[0].totalDue ?? 0 : 0,
            b?.bkgCurrency
          ),
          { optionRender: ResCardLineOptionType.BigTotal,
            actions: finacialActions }
        )
      );

      if (
        rescomService.currentReservationPermission
          .getValue()
          .isDenied(ReservationAction.CanNotSeeCommission).isAllowed
      ) {
        if (!b.isOpenDeposit) {
          //NET Commission
          const netCommission = b.financialRecap[0]?.netCommission;
          if (netCommission != null) {
            card.lines.push(
              new ResCardLine(
                'NET Commission',
                coreFormatService.CurrencyAmount(netCommission, b?.bkgCurrency),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
          }
          //VAT Commission
          const vatCommission = b.financialRecap[0]?.vatCommission;
          if (vatCommission != null) {
            card.lines.push(
              new ResCardLine(
                'VAT Commission',
                coreFormatService.CurrencyAmount(vatCommission, b?.bkgCurrency),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
          }
          //Total Commission (Commission prepaid)
          const commission = b.financialRecap[0]?.commission;
          if (commission != null) {
            card.lines.push(
              new ResCardLine(
                // 'Total Commission' + (b.financialRecap[0].prePaid ? ' (Commission prepaid)': ''),
                'Total Commission' +
                (b.financialRecap[0].prePaid ? ' (Commission prepaid)' : ''),
                coreFormatService.CurrencyAmount(commission, b?.bkgCurrency),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
          }
          //Commission paid
          const commissionPaid = b.financialRecap[0]?.commissionPaid;
          if (commissionPaid != null) {
            card.lines.push(
              new ResCardLine(
                'Commission paid',
                coreFormatService.CurrencyAmount(
                  commissionPaid,
                  b?.bkgCurrency
                ),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
          }

          //Booking balance
          const bookingBalance = b.financialRecap[0]?.bookingBalance;
          if (bookingBalance != null) {
            card.lines.push(
              new ResCardLine(
                'Booking balance',
                coreFormatService.CurrencyAmount(
                  bookingBalance,
                  b?.bkgCurrency
                ),
                { optionRender: ResCardLineOptionType.BigTotal }
              )
            );
          }

          if(b.isAandK && !b.isDMC) {
            //NetMargin
            const marginNet = b.financialRecap[0]?.marginNet;
            if (marginNet != null) {
              card.lines.push(
                new ResCardLine(
                  'Net Margin',
                  coreFormatService.CurrencyAmount(
                    marginNet,
                    b?.bkgCurrency
                  ),
                  { optionRender: ResCardLineOptionType.Highlight }
                )
              );
            }
            //GrossMargin
            const marginGross = b.financialRecap[0]?.marginGross;
            if (marginGross != null) {
              card.lines.push(
                new ResCardLine(
                  'Gross Margin',
                  coreFormatService.CurrencyAmount(
                    marginGross,
                    b?.bkgCurrency
                  ),
                  { optionRender: ResCardLineOptionType.Highlight }
                )
              );
            }
          }

        }
      }
    }

    cards.push(card);
    return cards;
  }

  checkSupplmentIsFirst(supplement:BookingJourneySupplement|BookingSupplement,serviceTypeID:number) {
    if(supplement.serviceTypeID !== serviceTypeID) return false
    if(!this.hasCurrentBooking) return false
    const bkg = this.current.getValue()
    if(!supplement.itineraryDate) return false
    //if(!bkg.travelStartDate) return false
    //if(supplement.itineraryDate.startOf('day') > bkg.travelStartDate.startOf('day')) return false
    const s = bkg.journeySupplements.filter(s=>s.serviceTypeID===supplement.serviceTypeID && s.itineraryDate?.isValid)
                                    .find(s=>s.itineraryDate!.startOf('day')<supplement.itineraryDate!.startOf('day'))
    if(s) return false
    return true
  }
  checkSupplmentIsLast(supplement:BookingJourneySupplement|BookingSupplement,serviceTypeID:number) {
    if(supplement.serviceTypeID !== serviceTypeID) return false
    if(!this.hasCurrentBooking) return false
    const bkg = this.current.getValue()
    if(!supplement.itineraryDate) return false
    //if(!bkg.travelEndDate) return false
    //const endDate = bkg.travelEndDate.minus({day: 1}).startOf('day')
    //if(supplement.itineraryDate.startOf('day') < endDate) return false
    const s = bkg.journeySupplements.filter(s=>s.serviceTypeID===supplement.serviceTypeID && s.itineraryDate?.isValid)
                                    .find(s=>s.itineraryDate!.startOf('day')>supplement.itineraryDate!.startOf('day'))
    if(s) return false
    return true
  }

  loadCardsForBooking(
    coreFormatService: CoreFormatService,
    eidosConfigService: EidosConfigService,
    checkoutMode: boolean = false,
    resService: ReservationService,
    rescomService: RescomService,
    startClosed: string[] = []
  ): BookingSectionResCard[] {
    const sidebarCards: BookingSectionResCard[] = [];
    const booking = this.current.getValue();
    let card = new ResCard();
    card = new ResCard();

    const iconFinacialDetails = {
      iconType: EidosIconType.Awesome,
      iconCode: "fa-plus-circle",
      iconSize: 0.6,
    }
    const finacialActions:IResCardLineOptionsActions[] = [
      {
        icon:iconFinacialDetails,
        callback:resService.bookingUtility.viewFinacialDetails.bind(this)
      }
    ]
    if (!booking.isOpenDeposit) {
      card.title = 'GENERAL INFO';

      card.lines.push(new ResCardLine('Status', booking.bkgStatus ?? ''));
      // card.lines.push(new ResCardLine('Type', booking.bkgType ?? ''));
      card.lines.push(new ResCardLine('Company', booking.companyName ?? ''));
      if(booking.canEditBrandName) card.lines.push(new ResCardLine('Brand', booking.brandName ?? ''));
      card.lines.push(
        new ResCardLine('Price Type', booking.priceTypeName ?? '')
      );
      card.lines.push(new ResCardLine('Currency', booking.bkgCurrency ?? ''));

      if (booking.getAllGuests().find((g) => g.checkBooking)?.checkBooking) {
        card.lines.push(
          new ResCardLine(
            '',
            `! Guests have another booking on the same voyage`,
            { ishighlighted: true } as IResCardLineOptions
          )
        );
      }

      if (booking.gifComplete === ResBookingGifFlag.Completed) {
        card.lines.push(
          new ResCardLine('', `Guest's GIFs is completed. Thanks`)
        );
      } else if (booking.gifComplete === ResBookingGifFlag.NotCompleted) {
        const advise = booking.owner.isDirect()
          ? 'your Guests'
          : 'Travel Agent or Guests';
        card.lines.push(
          new ResCardLine(
            '',
            `! Please note GIF has not been completed yet – Please advise ${advise}`,
            { ishighlighted: true } as IResCardLineOptions
          )
        );
      }

      if (booking.shorexLabel) {
        card.lines.push(
          new ResCardLine(
            '',
            `! ${booking.shorexLabel}`,
            { ishighlighted: true } as IResCardLineOptions
          )
        );
      }

      // if (booking.travelStartDate && booking.travelStartDate.isValid) {
      //   this.dateStart = booking.travelStartDate;
      // }

      // if (booking.travelEndDate && booking.travelEndDate.isValid) {
      //   this.dateEnd = booking.travelEndDate;
      // }

      const dateStartFormatted: string = !!booking.travelStartDate
        ? booking.travelStartDate.toFormat(
          coreFormatService.DateFmtWithMonthName()
        )
        : '';
      const dateEndFormatted: string = !!booking.travelEndDate
        ? booking.travelEndDate!.toFormat(
          coreFormatService.DateFmtWithMonthName()
        )
        : '';

      const duration = (booking.travelEndDate?.diff(booking.travelStartDate!, ['days']).days ?? 0) + 1
      card.lines.push(
        new ResCardLine('Date', `${dateStartFormatted} - ${dateEndFormatted} (duration: ${duration} days)`)
      );

      booking.sections.forEach((s) => {
        if (s.guestNum > 0) {
          card.lines.push(
            new ResCardLine('Guest N.', s.guestNum.toString(), {
              status: s.sectionID.toString(),
            } as IResCardLineOptions)
          );
        }
      });

      if (booking.isDeleted()) {
        if (booking.cancelDate && booking.cancelDate.isValid) {
          const cancelDateFormatted: string = booking.cancelDate.toFormat(
            coreFormatService.DateFmtWithMonthName()
          );
          card.lines.push(new ResCardLine('Cancel Date', cancelDateFormatted));
        }
      } else {
        if (booking.depositDate && booking.depositDate.isValid) {
          const paymentDueDateFormatted: string = booking.depositDate.toFormat(
            coreFormatService.DateFmtWithMonthName()
          );
          card.lines.push(
            new ResCardLine('Payment Due Date', paymentDueDateFormatted)
          );
        }

        const daysToTravel = booking.daysToTravelStartDate();
        if(daysToTravel){
          card.lines.push(
            new ResCardLine(
              'Days to Travel',
              daysToTravel > 0 ? daysToTravel.toString() : 'STARTED'
            )
          );
        }
      }

      if (booking.segment){
        card.lines.push(new ResCardLine('Segment', booking.segment))
      }
      if (booking.subSegment){
        card.lines.push(new ResCardLine('Sub Segment', booking.subSegment))
      }
      if (booking.mainDestination){
        card.lines.push(new ResCardLine('Main Destination', booking.mainDestination))
      }

      if (booking.creationDate) {
        card.lines.push(
          new ResCardLine(
            'Created',
            `${booking.createdBy} on ${booking.creationDate?.toFormat(
              coreFormatService.DateFmtWithMonthName()
            )}`
          )
        );
        if (booking.hasUpdateData()) {
          card.lines.push(
            new ResCardLine(
              'Updated',
              `${booking.updatedBy} on ${booking.updated?.toFormat(
                coreFormatService.DateFmtWithMonthName()
              )}`
            )
          );
        }
      }
      if (booking.note) {
        card.lines.push(
          new ResCardLine('Note', booking.note, {
            truncateLines: 3,
            tooltip: booking.note,
          } as IResCardLineOptions)
        );
      }
      if (booking.confirmationDate) {
        card.lines.push(
          new ResCardLine(
            'Confirmed',
            booking.confirmationDate?.toFormat(
              coreFormatService.DateFmtWithMonthName()
            )
          )
        );
      }

      if (booking.grpID ?? 0 > 0) {
        const hyperlink = {
          url: eidosConfigService.getLinks('groupmaster')?.template,
          target: '_blank',
          parameters: { id: booking.grpID },
        } as IResCardLineOptionsHyperlink;
        card.lines.push(
          new ResCardLine(
            'Group',
            booking.grpName.length > 0
              ? `${booking.grpName} - (${booking.grpID})`
              : `${booking.grpID}`,
            { hyperlink: hyperlink } as IResCardLineOptions
          )
        );
      }
      if (booking.bkgIDTOC>0) {
        const bkgUrl:IResCardLineOptionsHyperlink = {
        url: `${window.location.origin}/reservation/booking/${booking.bkgIDTOC}`,
        target: '_blank',
        parameters: { },
      }

        card.lines.push(
          new ResCardLine(
            'ICO Booking',
            `${booking.bkgIDTOC}`,{
              hyperlink: bkgUrl,
            } as IResCardLineOptions
          )
        );
      }

      sidebarCards.push(new BookingSectionResCard(0, card));
    }

    card = new ResCard();
    card.title = 'BOOKING OWNER';

    if (booking.owner) {
      if(booking.goodwill) {
        card.lines.push(
          new ResCardLine('Booking Name', booking.bkgName)
        );

      }
      const hyperlink = {
        url: eidosConfigService.getLinks('entitymaster')?.template,
        target: '_blank',
        parameters: { entity: 'agency', id: booking.owner.agencyID },
      } as IResCardLineOptionsHyperlink;

      const hyperlinkAgent = {
        url: eidosConfigService.getLinks('entitymaster')?.template,
        target: '_blank',
        parameters: { entity: 'customer', id: booking.owner.individualID },
      } as IResCardLineOptionsHyperlink;

      if (booking.owner.isAgent()) {
        card.lines.push(
          new ResCardLine('Agency', booking.owner.displayName(), {
            hyperlink: hyperlink,
          } as IResCardLineOptions)
        );
        // Add IATA and Agency to the booking owner screen
        if (booking.owner.agencyIataCod) {
          card.lines.push(
            new ResCardLine('IATA Code', booking.owner.agencyIataCod ?? '')
          );
        }

        if (booking.owner.agencyGroup) {
          card.lines.push(
              new ResCardLine('Agency Group', booking.owner.agencyGroup ?? '')
          );
        }
        if (booking.owner.individualID) {
          card.lines.push(
            new ResCardLine('Contact', booking.owner.displayContactName(), {
              hyperlink: hyperlinkAgent,
            } as IResCardLineOptions)
          );
        } else {
          card.lines.push(
            new ResCardLine('Contact', booking.owner.displayContactName())
          );
        }
      }
      //Direct booking
      else {
        if (booking.owner.individualID) {
          card.lines.push(
            new ResCardLine('Direct', booking.owner.displayName(), {
              hyperlink: hyperlinkAgent,
            } as IResCardLineOptions)
          );
        }
        else {
          card.lines.push(new ResCardLine('Direct', booking.owner.displayName()));
        }
      }

      // RF - 30.08.2022: Federico's request
      if (!checkoutMode) {
        let address = booking.owner.addressToDisplay;
        if (booking.owner.address2) {
          address += `${!!booking.owner.addressToDisplay ? ' - ' : ''
            } ${!!booking.owner.address2}`;
        }
        card.lines.push(new ResCardLine('Address', address ?? ''));
        card.lines.push(
          new ResCardLine('Country', booking.owner.country ?? '')
        );
      }

      card.lines.push(new ResCardLine('Email', booking.owner.email ?? ''));
      card.lines.push(new ResCardLine('Phone', booking.owner.phone ?? ''));

      if (booking.isOpenDeposit && booking.owner?.displayGuest) {
        card.lines.push(new ResCardLine('Guest', booking.owner.guest ?? ''));
      }
    }
    sidebarCards.push(new BookingSectionResCard(0, card));

    // RF - 30.08.2022: Hide financial recap in checkout as requested by Federico
    if (!checkoutMode) {
      function loadSessionFinancialRecap(
        sectionID: number,
        subtitle: boolean = false
      ) {

        card = new ResCard();
        card.title = `FINANCIAL RECAP ${booking.financialRecap[0]?.commission && booking.commissionPaymentType ? `<span style="font-size:0.8em">(Commission Paid as: ${booking.commissionPaymentType})</span>` : ''}`;
        card.titleActions = booking.multiCurrencyBkg
          ? [
            {
              icon: iconFinacialDetails,
              callback: () => resService.bookingUtility.viewFinacialDetailsMultiBkg(),
            },
          ]
          : [];
        if (subtitle)
          card.lines.push(
            ResCardLine.createSubtitle(
              sectionID != undefined ? 'VERSION ' + sectionID : ''
            )
          );

        booking.financialRecap
          .filter((f) => f.sectionID == sectionID)
          .forEach((f) => {
            card.lines.push(
              new ResCardLine(
                'Total Charge',
                coreFormatService.CurrencyAmount(
                  f.totalCharge ?? 0,
                  booking?.bkgCurrency
                ),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
            card.lines.push(
              new ResCardLine(
                'Total Paid',
                coreFormatService.CurrencyAmount(
                  f.totalPayment ?? 0,
                  booking?.bkgCurrency
                ),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );

            //Total Due solo per GROSS PAYER
            if (!booking.financialRecap[0].prePaid) {
              card.lines.push(
                new ResCardLine(
                  'Total Due',
                  coreFormatService.CurrencyAmount(
                    f.totalDue ?? 0,
                    booking?.bkgCurrency
                  ),
                  { optionRender: ResCardLineOptionType.BigTotal,
                    actions: finacialActions,
                  }
                )
              );
            }

            if (!booking.isOpenDeposit) {
              if (
                rescomService.currentReservationPermission
                  .getValue()
                  .isDenied(ReservationAction.CanNotSeeCommission).isAllowed
              ) {
                //Caso GROSS PAYER
                if (!booking.financialRecap[0].prePaid) {
                  //NET Commission
                  if (f.netCommission != null) {
                    card.lines.push(
                      new ResCardLine(
                        'NET Commission',
                        coreFormatService.CurrencyAmount(
                          f.netCommission,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                  //VAT Commission
                  if (f.vatCommission != null) {
                    card.lines.push(
                      new ResCardLine(
                        'VAT Commission' + (f.vatCommissionPercentage ? ` (${f.vatCommissionPercentage}%)` : ''),
                        coreFormatService.CurrencyAmount(
                          f.vatCommission,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                  //Total Commission
                  if (f.commission != null) {
                    card.lines.push(
                      new ResCardLine(
                        'Total Commission',
                        coreFormatService.CurrencyAmount(
                          f.commission ?? 0,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                  //Commission paid
                  if (f.commissionPaid != null) {
                    card.lines.push(
                      new ResCardLine(
                        'Commission paid',
                        coreFormatService.CurrencyAmount(
                          f.commissionPaid,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                  //Booking balance
                  if (f.bookingBalance != null) {
                    card.lines.push(
                      new ResCardLine(
                        'Booking balance',
                        coreFormatService.CurrencyAmount(
                          f.bookingBalance,
                          booking?.bkgCurrency
                        ),
                        { optionRender: ResCardLineOptionType.BigTotal }
                      )
                    );
                  }

                  if(booking.isAandK && !booking.isDMC) {
                    //NetMargin
                    const marginNet = booking.financialRecap[0]?.marginNet;
                    if (marginNet != null) {
                      card.lines.push(
                        new ResCardLine(
                          'Net Margin',
                          coreFormatService.CurrencyAmount(
                            marginNet,
                            booking?.bkgCurrency
                          ),
                          { optionRender: ResCardLineOptionType.Highlight }
                        )
                      );
                    }
                    //GrossMargin
                    const marginGross = booking.financialRecap[0]?.marginNet;
                    if (marginGross != null) {
                      card.lines.push(
                        new ResCardLine(
                          'Gross Margin',
                          coreFormatService.CurrencyAmount(
                            marginGross,
                            booking?.bkgCurrency
                          ),
                          { optionRender: ResCardLineOptionType.Highlight }
                        )
                      );
                    }
                  }

                } else {

                  //
                  //Caso NET PAYER
                  //

                  //NET Commission
                  if (f.netCommission != null) {
                    card.lines.push(
                      new ResCardLine(
                        'NET Commission prepaid',
                        coreFormatService.CurrencyAmount(
                          f.netCommission,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                  //VAT Commission
                  if (f.vatCommission != null) {

                    card.lines.push(
                      new ResCardLine(
                        'VAT Commission prepaid' + (f.vatCommissionPercentage ? ` (${f.vatCommissionPercentage}%)` : ''),
                        coreFormatService.CurrencyAmount(
                          f.vatCommission,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }

                  //Total Commission prepaid
                  if (f.commission != null) {
                    card.lines.push(
                      new ResCardLine(
                        'Total Commission prepaid',
                        coreFormatService.CurrencyAmount(
                          f.commission ?? 0,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }

                  //Total Due solo per Net PAYER
                  card.lines.push(
                    new ResCardLine(
                      'Total Due',
                      coreFormatService.CurrencyAmount(
                        f.totalDue ?? 0,
                        booking?.bkgCurrency
                      ),
                      { optionRender: ResCardLineOptionType.BigTotal,
                        actions: finacialActions,
                      }
                    )
                  );

                  //Commission payment uploaded
                  if (f.commissionPaid != null) {
                    card.lines.push(
                      new ResCardLine(
                        'Commission payment uploaded',
                        coreFormatService.CurrencyAmount(
                          f.commissionPaid,
                          booking?.bkgCurrency
                        ),
                        {
                          optionRender: ResCardLineOptionType.AlignRight,
                        } as IResCardLineOptions
                      )
                    );
                  }
                }
              }
              else {
                //Aggiunta Total Due solo per Net PAYER con CanNotSeeCommission
                if (booking.financialRecap[0].prePaid) {
                  //Caso NET PAYER
                  card.lines.push(
                    new ResCardLine(
                      'Total Due',
                      coreFormatService.CurrencyAmount(
                        f.totalDue ?? 0,
                        booking?.bkgCurrency
                      ),
                      { optionRender: ResCardLineOptionType.BigTotal,
                        actions: finacialActions,
                      }
                    )
                  );
                }
              }
            }

            sidebarCards.push(new BookingSectionResCard(sectionID, card));
          });
      }

      if (booking.sections.length === 1) {
        loadSessionFinancialRecap(booking.sections[0].sectionID);
      } else {
        booking.sections.forEach((s) =>
          loadSessionFinancialRecap(s.sectionID, true)
        );
      }

      //booking.Deposit
      function loadSessionPaymentSchedule(
        sectionID: number,
        subtitle: boolean = false
      ) {
        card = new ResCard();
        card.title = 'PAYMENT SCHEDULE';
        const canEditPaymentMilestones =
          getRootInjector(EidosSecurityService).isOnByLevel('uat')
          && rescomService.currentReservationPermission
            .getValue()
            .canIEditOnBooking(ReservationEditActionBooking.EditBookingDepositDate, booking).isAllowed
          && !!booking.canExtendDepositDate;
        if (canEditPaymentMilestones) {
          card.titleActions = [
            {
              icon: {
                iconType: EidosIconType.Awesome,
                iconCode: 'fa-search',
                iconSize: 0.6,
              },
              tooltip: 'View / Edit payment milestones',
              callback: () => resService.bookingUtility.viewPaymentMilestones(),
            },
          ];
        }
        if (subtitle)
          card.lines.push(
            ResCardLine.createSubtitle(
              sectionID != undefined ? 'VERSION ' + sectionID : ''
            )
          );
        booking.deposit
          .filter((d) => d.sectionID === sectionID)
          .filter((d) => d.paymentDueDate && d.paymentDueDate.isValid)
          .forEach((d) => {
            card.lines.push(
              new ResCardLine(
                d.paymentDueDate!.toFormat(
                  coreFormatService.DateFmtWithMonthName()
                ),
                (d.displayText + ' ' + coreFormatService.CurrencyAmount(d.amount ?? 0, d.currency)),
                {
                  optionRender: ResCardLineOptionType.AlignRight,
                } as IResCardLineOptions
              )
            );
          });
        sidebarCards.push(new BookingSectionResCard(sectionID, card));
      }
      if (booking.sections.length === 1) {
        loadSessionPaymentSchedule(booking.sections[0].sectionID);
      } else {
        booking.sections.forEach((s) =>
          loadSessionPaymentSchedule(s.sectionID, true)
        );
      }
    }
    // + d.includeInsurance ? '(with Insurance)' : '(without Insurance)'
    if (booking.penalties && !booking.isOpenDeposit) {
      function loadSessionPenalties(sectionID: number) {
        card = new ResCard();
        card.title = 'PENALTIES (Excluding Destination Fees)';
        let breakKey = '';
        booking.penalties
          .filter((p) => p.sectionID === sectionID)
          .forEach((p) => {
            const daysInNumbers =
              p.daysFrom != null && p.daysTo != null
                ? `${p.daysFrom} / ${p.daysTo}`
                : '';
            const daysInDates =
              p.dateFrom?.isValid && p.dateTo?.isValid
                ? `${p.dateFrom.toFormat(
                  coreFormatService.DateFmtWithMonthName()
                )} / ${p.dateTo.toFormat(
                  coreFormatService.DateFmtWithMonthName()
                )}`
                : '';

            const caption =
              daysInNumbers || daysInDates
                ? `${daysInNumbers} (${daysInDates})`
                : '-';
            const value = p.fixedAmount
              ? coreFormatService.CurrencyAmount(
                p.fixedAmount,
                booking?.bkgCurrency
              )
              : `${coreFormatService.PercentualNumber(
                p.amountPerc ?? 0,
                '1.0-0'
              )} (${coreFormatService.CurrencyAmount(
                p.amountValue ?? 0,
                booking?.bkgCurrency
              )})`;
            const newKey = `${p.groupID}-${p.packageID}-${p.sectionID}-${p.optionID}-${p.voyageID}-${p.serviceLongName}`;
            if (newKey !== breakKey) {
              breakKey = newKey;

              function getVoyageDesc(
                voyageID: number,
                voyageName: string
              ): string {
                const voyages = booking.getAllVoyages();
                if (voyages.length < 2) return voyageName;
                const voy = booking
                  .getAllVoyages()
                  .find((v) => v.voyageID === voyageID);
                if (!voy) return voyageName;
                return (
                  voy.displayName() +
                  (voy.embarkDate && voy.embarkDate.isValid
                    ? ' - embark: ' +
                    voy.embarkDate.toFormat(
                      coreFormatService.DateFmtWithMonthName()
                    )
                    : '')
                );
              }

              if (p.groupID && booking.multiSuite) {
                const groupDesc = p.groupID===-10 ? 'AGENT' : `${p.groupID}`
                card.lines.push(
                  ResCardLine.createSubtitle(`Group ${groupDesc}`)
                );
              }

              if (p.packageID && (p.packageName ?? '') !== '') {
                card.lines.push(
                  ResCardLine.createSubtitle(p.packageName ?? '')
                );
              }

              if (p.sectionID && (p.serviceLongName ?? '') !== '') {
                if (p.packageName !== p.serviceLongName) {
                  card.lines.push(
                    ResCardLine.createSubtitle(p.serviceLongName ?? '')
                  );
                }
              }
              if (p.optionID && (p.optionName ?? '') !== '') {
                if (p.optionName !== p.packageName && p.optionName !== p.serviceLongName) {
                  card.lines.push(
                    ResCardLine.createSubtitle(p.optionName ?? '')
                  );
                }
              }
              if (p.voyageID) {
                const subtitle = getVoyageDesc(p.voyageID, p.voyageName ?? '');
                card.lines.push(ResCardLine.createSubtitle(subtitle));
              }
            }

            const currentDate = DateTime.local().set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            });
            const isCurrentDateBetweenPenaltyDates =
              booking.isBooked() &&
              p.dateFrom?.isValid &&
              p.dateTo?.isValid &&
              p.dateFrom <= currentDate &&
              (p.dateTo >= currentDate || p.dateTo <= currentDate && p.amountPerc === 100 ) //if penalty is 100% and dateTo is in the past, it is still valid

              //&& (p.packageID === 3 || p.packageID === 11 || p.packageID === 16 )
              //only for Accomodation, Flights and Transport

            card.lines.push(
              new ResCardLine(caption, value, {
                optionRender: ResCardLineOptionType.AlignRight,
                ishighlighted: isCurrentDateBetweenPenaltyDates,
              } as IResCardLineOptions)
            );
          });

          sidebarCards.push(new BookingSectionResCard(sectionID, card));
      }
      booking.sections.forEach((s) => loadSessionPenalties(s.sectionID));
    }

    if (booking.isOpenDeposit) {
      card = new ResCard();
      card.title = 'OTHER INFO';

      card.lines.push(new ResCardLine('Notes', booking.note));

      if (booking.bkgAgentOwner) {
        card.lines.push(new ResCardLine('Agent owner', booking.bkgAgentOwner));
      }
      sidebarCards.push(new BookingSectionResCard(0, card));
    }

    sidebarCards.flatMap(sc=>sc.card)
                .forEach(card=>{
                  card.startClosed = startClosed.includes(card.title)
                })

    this.lastSidebarCards.next(sidebarCards)
    return sidebarCards;
  }
}

export enum SectionView {
  BookingJourney,
  Guests,
  DayByDay,
  ItemsByGuests,
  ItemsByTravelGroups,
  ItemsByService,
  ItemsBySuppliers,
  ItemsByAll,
}

export class ResItemEditRules {
  bkg:ResItemAmounts = {
    currencyCod: '',
    price: 0,
    originalPrice: 0,
    tax:0,
    originalTax: 0,
    roe:0,
    vat: 0,
    vatAmount: 0,
    marginAmount:0,
    marginPercentage:0,
    originalMarginPercentage: 0,
  }
  selling:ResItemAmounts = {
    currencyCod: '',
    price: 0,
    originalPrice: 0,
    tax:0,
    originalTax: 0,
    roe:0,
    vat: 0,
    vatAmount: 0,
    marginAmount:0,
    marginPercentage:0,
    originalMarginPercentage: 0,
  }
  cost:ResItemAmounts = {
    currencyCod: '',
    price: 0,
    originalPrice: 0,
    tax:0,
    originalTax: 0,
    roe:0,
    vat: 0,
    vatAmount: 0,
    marginAmount:0,
    marginPercentage:0,
    originalMarginPercentage: 0,
  }
  actualCost:ResItemAmounts = {
    currencyCod: '',
    price: 0,
    originalPrice: 0,
    tax:0,
    originalTax: 0,
    roe:0,
    vat: 0,
    vatAmount: 0,
    marginAmount:0,
    marginPercentage:0,
    originalMarginPercentage: 0,
  }
  commission:ResItemCommission = {
    baseAmount: 0,
    percentage: 0,
    vatPercentage: 0,
    net:0,
    vat:0,
    total:0,
  }
  includeVat = true
  vat = 0
  isDmcBooking = false
  isDmcIntraBooking = false
  quantity=0
  originalQuantity=0
  commissionable = false
  viewActualCost = false
  marginType = ''

  static allMarginType = [
    {code: 'NET', description: 'Net'},
    {code: 'GROSS', description: 'Gross'},
  ]
  get marginPercentageNormalized() {
    if(this.bkg.marginPercentage===0) return 1
    return (1+this.bkg.marginPercentage/100)
  }
  get marginNewPercentageNormalized() {
    if(this.bkg.marginPercentage===0) return 1
    if(this.bkg.marginPercentage===100) return (1-99.99/100)
    return (1-this.bkg.marginPercentage/100)
  }
  retriveCost() {
    if(this.cost.roe===0) this.cost.roe = 1
    let cost = this.cost.price * this.cost.roe
    if(this.includeVat) cost += this.cost.tax * this.cost.roe
    if(this.marginType === 'NET') return cost
    return cost + this.cost.tax * this.cost.roe
  }
  retriveSellingCost() {
    if(this.selling.roe===0) this.selling.roe = 1
    let cost = this.cost.price * this.cost.roe
    if(this.includeVat) cost += this.cost.tax * this.cost.roe
    if(this.marginType === 'NET') return cost
    return cost + this.cost.tax * this.cost.roe
  }
  retriveActualCost() {
    if(this.actualCost.roe===0) this.actualCost.roe = 1
    if(this.marginType === 'NET') return this.actualCost.price * this.actualCost.roe
    return this.actualCost.price * this.actualCost.roe + this.actualCost.tax * this.actualCost.roe
  }

  calculateMargine(force=false) {
    const cost = this.retriveCost()
    //this.bkg.marginAmount = this.bkg.price - cost
    this.bkg.marginAmount = this.bkg.price - cost
    if(this.bkg.marginPercentage===0 || force) this.calculateMarginNewPercentage()
    this.calculateActualMargin()
  }
  calculateMarginNewPercentage() {
    if(this.bkg.price===0) {
      this.bkg.marginPercentage = 0
      return
    }

    if(this.bkg.marginAmount===0) {
      this.bkg.marginPercentage = 0
      return
    }
    const cost = this.retriveCost()
    this.bkg.marginAmount =  this.bkg.price-cost
    //this.bkg.marginPercentage = cost === 0 || this.bkg.marginAmount === 0 ? 0 : this.round2Digit(this.bkg.marginAmount / cost * 100)
    if(cost !== 0 && this.bkg.price>0) {
      this.bkg.marginPercentage = this.round2Digit((1 - cost / this.bkg.price) * 100)
    }
  }
  calculateMarginOldPercentage() {
    if(this.bkg.marginAmount===0) {
      this.bkg.marginPercentage = 0
      return
    }
    const cost = this.retriveCost()
    this.bkg.marginPercentage = cost === 0 ? 0 : this.round2Digit(this.bkg.marginAmount / this.bkg.price * 100)
  }
  getVatAmount() {
    if(this.cost.roe===0) this.cost.roe = 1
    return this.cost.tax * this.cost.roe
  }
  calculateActualMargin() {
    const cost = this.retriveActualCost()
    this.actualCost.marginAmount = this.bkg.price - cost
    this.actualCost.marginPercentage = cost === 0 ? 0 : this.round2Digit(this.actualCost.marginAmount / cost * 100)
  }
  calculateCost() {
    if(this.cost.price===0 && this.bkg.marginPercentage>0 && this.bkg.price > 0) {
      if(this.cost.roe===0) this.cost.roe = 1
      if(this.bkg.marginPercentage > 0 ){
        if(this.marginNewPercentageNormalized>0) {
          const cost = this.round2Digit(this.bkg.price * this.marginNewPercentageNormalized) / this.cost.roe
          this.cost.price = cost
        }
      }
    }
  }
  calculateCostOld() {
    if(this.cost.price===0 && this.bkg.marginPercentage>0 && this.bkg.price > 0) {
      if(this.cost.roe===0) this.cost.roe = 1
      if(this.bkg.marginPercentage > 0 ){
        const cost = this.round2Digit(this.bkg.price / this.marginPercentageNormalized) / this.cost.roe
        this.cost.price = cost
      }
    }
  }
  // calculatePriceOld(forceRecalulateMargin=false) {
  //   if(this.cost.price>0) {
  //     if(this.cost.roe===0) this.cost.roe = 1
  //     const cost = this.retriveCost()
  //     if(this.bkg.marginPercentage<=0) this.actualCost.marginPercentage = 0
  //     //NEW RULES 2025.08.08
  //     if(this.bkg.marginPercentage > 0 && this.cost.price > 0){
  //       //const price =
  //       const price = this.round2Digit(cost * this.marginPercentageNormalized)
  //       this.bkg.price = price
  //     }
  //     if(this.marginType === 'NET') {
  //       this.bkg.tax = this.cost.tax * this.cost.roe
  //     } else {
  //       this.bkg.tax = this.round2Digit(this.cost.tax * this.cost.roe * this.marginPercentageNormalized)
  //     }
  //   }
  //   this.commission.baseAmount = this.bkg.price
  //   this.calculateSellingPrice()
  //   this.calculateMargine(forceRecalulateMargin)
  //   this.calculateCommission()
  // }
  calculateCostTax() {
    if(!this.includeVat && this.cost.vat > 0 && this.cost.price > 0) {
      this.cost.tax = this.cost.price * this.cost.vat/100;
    }
  }
  calculateVatPerc() {
    if(this.cost.price > 0 && this.cost.tax > 0) {
      this.cost.vat = this.cost.tax  / this.cost.price*100;
    }
  }
  calculatePrice(forceRecalulateMargin=false) {
    if(this.cost.price>0) {
      if(this.cost.roe===0) this.cost.roe = 1
      const cost = this.retriveCost()
      if(this.bkg.marginPercentage<=0) this.actualCost.marginPercentage = 0
      //NEW RULES 2025.08.08
      if(this.marginNewPercentageNormalized > 0 && this.cost.price > 0){
        //const price =
        const price = this.round2Digit(cost / this.marginNewPercentageNormalized)
        this.bkg.price = price
        if(!this.includeVat && this.bkg.vat > 0) {
          this.bkg.tax = this.round2Digit((this.bkg.price*this.bkg.vat/100))
          this.bkg.price += this.bkg.tax
          this.calculateSellingPrice()
          this.selling.tax = this.round2Digit((this.selling.price*this.selling.vat/100))
          this.selling.price += this.selling.tax
        } else {
          this.bkg.tax = 0
          this.selling.tax = 0
        }
      }
      // if(this.includeVat) {
      //   //REMOVE TOBE CHECK
      //   // if(this.marginType === 'NET') {
      //   //   this.bkg.tax = this.cost.tax * this.cost.roe
      //   // } else {
      //   //   this.bkg.tax = this.round2Digit(this.cost.tax * this.cost.roe / this.marginNewPercentageNormalized)
      //   // }
      //   this.calculateSellingPrice()
      // }
    }
    this.commission.baseAmount = this.bkg.price
    this.calculateMargine(forceRecalulateMargin)
    this.calculateCommission()
  }
  calculateSellingPrice() {
    if(this.selling.currencyCod===this.cost.currencyCod && this.marginPercentageNormalized===1) {
      this.selling.price = this.cost.price
      this.selling.tax = this.cost.tax
    } else {
      const roe = this.selling.roe>0 && this.selling.currencyCod!==this.bkg.currencyCod ? this.selling.roe : 1
      this.selling.price = this.bkg.price * roe
      this.selling.tax = this.bkg.tax * roe
    }
  }
  calculateCommission() {
    if(this.commissionable) {
      this.commission.baseAmount = this.bkg.price
      this.commission.net = this.commission.percentage ? this.applyPercentage(this.commission.baseAmount, this.commission.percentage) : 0
      this.commission.vat = this.commission.vatPercentage>0 ? this.applyPercentage(this.commission.net,this.commission.vatPercentage) : 0
      this.commission.total = this.commission.net+this.commission.vat
    } else {
      this.commission.baseAmount = this.bkg.price
      this.commission.net = 0
      this.commission.vat = 0
      this.commission.total = 0
    }
  }
  applyPercentage(baseAmount:number, perc:number) {
    if(perc===0) return baseAmount
    return this.round2Digit(baseAmount * perc / 100)
  }
  round2Digit(n:number) {
    return Math.round(n*100)/100
  }
  updateBkgItem(item:ResBkgItem) {
    item.commissionable = this.commissionable
    item.marginType = this.marginType

    item.sellingPrice = this.bkg.price
    item.sellingPriceCurrency = this.bkg.currencyCod
    item.sellingPriceTax = this.bkg.tax
    //this.itemEditRules.bkg.roe = 1
    item.marginAmount = this.bkg.marginAmount
    item.marginPercentage = this.bkg.marginPercentage
    //if(this.itemEditRules.bkg.roe===0) this.itemEditRules.bkg.roe = 1

    item.mcSellingPrice = this.selling.price
    item.mcSellingTax = this.selling.tax
    item.mcSellingCurrency = this.selling.currencyCod
    item.mcSellingROE = this.selling.roe
    //item.includeVat = this.includeVat
    //item.vat = this.vat

    item.cost = this.cost.price
    item.costCurrency = this.cost.currencyCod
    item.costTax = this.cost.tax
    item.costRoe = this.cost.roe

    item.quantity = this.quantity

    if(!item.forcedNetComm) {

    }
    item.commissionBaseAmount = this.commission.baseAmount
    item.commissionPercentage = this.commission.percentage
    item.commissionNet = this.commission.net
    item.commissionVat = this.commission.vat
    item.vatCommissionPercentage = this.commission.vatPercentage
    item.commissionTotal = this.commission.total
    item.supplements.forEach(s=>{
      if(item.guests.find(g=>g.guestCod===s.guestCod && g.isSelected)) {
        this.updateBkgSupplementQty(s)
        s.message = item.remark
        s.fileGuid = item.fileGuid
        s.fileId = item.fileId
        s.fileName = item.fileName
      }
    })
  }
  updateBkgSupplement(item:BookingSupplement) {
    item.commissionable = this.commissionable
    item.marginType = this.marginType
    item.includeVat = this.includeVat
    item.vat = this.vat

    item.sellingPrice = this.bkg.price
    item.sellingPriceCurrency = this.bkg.currencyCod
    item.sellingPriceTax = this.bkg.tax
    //this.itemEditRules.bkg.roe = 1
    item.marginAmount = this.bkg.marginAmount
    item.marginPercentage = this.bkg.marginPercentage
    //if(this.itemEditRules.bkg.roe===0) this.itemEditRules.bkg.roe = 1

    item.mcSellingPrice = this.selling.price
    item.mcSellingTax = this.selling.tax
    item.mcSellingCurrency = this.selling.currencyCod
    item.mcSellingROE = this.selling.roe
    // item.includeVat = this.includeVat
    // item.vat = this.vat

    item.cost = this.cost.price
    item.costCurrency = this.cost.currencyCod
    item.costTax = this.cost.tax
    item.exchangeRate = this.cost.roe

    if(item.quantityPP===0) item.quantityPP = this.quantity
    item.commissionBaseAmount = this.commission.baseAmount
    item.commissionPercentage = this.commission.percentage
    item.commission = this.commission.net
    item.commissionVat = this.commission.vat
    item.vatCommissionPercentage = this.commission.vatPercentage
    item.commissionTotal = this.commission.total
  }
  updateBkgSupplementQty(item:BookingSupplement) {
    item.commissionable = this.commissionable
    item.marginType = this.marginType
    item.includeVat = this.includeVat
    item.vat = this.vat

    item.sellingPrice = this.bkg.price * item.quantityPP
    item.sellingPriceCurrency = this.bkg.currencyCod
    item.sellingPriceTax = this.bkg.tax * item.quantityPP
    //this.itemEditRules.bkg.roe = 1
    item.marginAmount = this.bkg.marginAmount * item.quantityPP
    item.marginPercentage = this.bkg.marginPercentage
    //if(this.itemEditRules.bkg.roe===0) this.itemEditRules.bkg.roe = 1

    item.mcSellingPrice = this.selling.price * item.quantityPP
    item.mcSellingTax = this.selling.tax * item.quantityPP
    item.mcSellingCurrency = this.selling.currencyCod
    item.mcSellingROE = this.selling.roe
    // item.includeVat = this.includeVat
    // item.vat = this.vat

    item.cost = this.cost.price * item.quantityPP
    item.costCurrency = this.cost.currencyCod
    item.costTax = this.cost.tax * item.quantityPP
    item.exchangeRate = this.cost.roe

    item.commissionBaseAmount = this.commission.baseAmount * item.quantityPP
    item.commissionPercentage = this.commission.percentage
    item.commission = this.commission.net
    item.commissionVat = this.commission.vat
    item.vatCommissionPercentage = this.commission.vatPercentage
    item.commissionTotal = this.commission.total
  }
  getTotalQuantity(item: ResBkgItem,gCodes?:string[]) {
    if(item.isTailorMade && item.quantity!==0) return item.quantity
    let items = item.supplements
    if(gCodes) items = item.supplements.filter(s=>!!gCodes.find(gCode=>gCode===s.guestCod))
    return items.reduce((t, s)=>t + s.quantityPP, 0)
  }
  initValuesFromSupplement(item:BookingSupplement, isDmcBooking:boolean, isDmcIntraBooking:boolean) {
    this.isDmcBooking = isDmcBooking
    this.isDmcIntraBooking = isDmcIntraBooking
    this.quantity = item.quantityPP
    this.originalQuantity = this.quantity
    this.marginType = item.marginType !== '' ? item.marginType : 'NET'

    this.bkg.price = item.sellingPrice
    this.bkg.originalPrice = item.sellingPrice
    this.bkg.currencyCod = item.sellingPriceCurrency
    this.bkg.tax = item.sellingPriceTax
    this.bkg.originalTax = item.sellingPriceTax
    this.bkg.roe = 1
    if(this.bkg.roe===0) this.bkg.roe = 1

    this.bkg.marginAmount = item.marginAmount
    this.bkg.marginPercentage = item.marginPercentage
    this.bkg.originalMarginPercentage = item.marginPercentage

    this.selling.price = item.mcSellingPrice
    this.selling.originalPrice = item.mcSellingPrice
    this.selling.tax = item.mcSellingTax
    this.selling.originalTax = item.mcSellingTax
    this.selling.currencyCod = item.mcSellingCurrency !== '' ? item.mcSellingCurrency: item.sellingPriceCurrency
    this.selling.roe = item.mcSellingROE
    if(this.selling.roe===0) this.selling.roe = 1

    this.actualCost.price = item.actualCost
    this.actualCost.currencyCod = item.actualCurrency !== '' ? item.actualCurrency: item.sellingPriceCurrency
    this.actualCost.tax = item.actualTax
    this.actualCost.roe = item.actualExchangeRate
    if(this.actualCost.roe===0) this.actualCost.roe = 1

    this.cost.price = item.cost
    this.cost.originalPrice = item.cost
    this.cost.currencyCod = item.costCurrency !== '' ? item.costCurrency: item.sellingPriceCurrency
    this.cost.tax = item.costTax
    this.cost.originalTax = item.costTax
    this.cost.roe = item.exchangeRate
    this.cost.vat = item.vat
    this.cost.vatAmount = this.getVatAmount()
    if(this.cost.roe===0) this.cost.roe = 1
    if(this.bkg.marginPercentage === 0) this.calculateMarginNewPercentage()
    else this.calculateActualMargin()


    this.commissionable = item.commissionable ?? false
    this.commission.baseAmount = item.commissionBaseAmount
    this.commission.percentage = item.commissionPercentage
    this.commission.vatPercentage = item.vatCommissionPercentage
    this.calculateCommission()
  }
  initValuesFromBkgItem(item: ResBkgItem, isDmcBooking:boolean, isDmcIntraBooking:boolean) {
    this.isDmcBooking = isDmcBooking
    this.isDmcIntraBooking = isDmcIntraBooking
    this.quantity = this.getTotalQuantity(item,item.guests.map(g=>g.guestCod))
    this.originalQuantity = this.quantity

    this.marginType = item.marginType !== '' ? item.marginType : 'NET'

    this.bkg.price = item.sellingPrice
    this.bkg.originalPrice = item.sellingPrice
    this.bkg.vat = item.vat

    this.bkg.currencyCod = item.sellingPriceCurrency
    this.bkg.tax = item.sellingPriceTax
    this.bkg.originalTax = item.sellingPriceTax

    this.bkg.roe = 1
    if(this.bkg.roe===0) this.bkg.roe = 1

    //@TODO from BE seems arrive % in margin and margin percentage don't arrive
    this.bkg.marginAmount = item.marginAmount
    this.bkg.marginPercentage = item.marginPercentage
    this.bkg.originalMarginPercentage = item.marginPercentage

    this.selling.price = item.mcSellingPrice
    this.selling.originalPrice = item.mcSellingPrice
    this.selling.tax = item.mcSellingTax
    this.selling.originalTax = item.mcSellingTax
    this.selling.currencyCod = item.mcSellingCurrency !== '' ? item.mcSellingCurrency: item.sellingPriceCurrency
    this.selling.roe = item.mcSellingROE
    this.selling.vat = item.vat

    this.vat = item.vat
    this.includeVat = item.includeVat
    if(this.selling.roe===0) this.selling.roe = 1

    this.actualCost.price = item.actualCost
    this.actualCost.originalPrice = item.actualCost
    this.actualCost.currencyCod = item.actualCurrency !== '' ? item.actualCurrency: item.sellingPriceCurrency
    this.actualCost.tax = item.actualTax
    this.actualCost.originalTax = item.actualTax
    this.actualCost.roe = item.actualExchangeRate
    if(this.actualCost.roe===0) this.actualCost.roe = 1

    this.cost.price = item.cost
    this.cost.originalPrice = item.cost
    this.cost.currencyCod = item.costCurrency !== '' ? item.costCurrency: item.sellingPriceCurrency
    this.cost.tax = item.costTax
    this.cost.originalTax = item.costTax
    this.cost.roe = item.costRoe
    this.cost.vat = item.vat
    this.cost.vatAmount = this.getVatAmount()
    if(this.cost.roe===0) this.cost.roe = 1
    this.calculateMargine()

    this.commissionable = item.commissionable
    this.commission.baseAmount = item.commissionBaseAmount
    this.commission.percentage = item.commissionPercentage
    this.commission.vatPercentage = item.vatCommissionPercentage
    this.calculateCommission()
  }
}

export type ResItemAmounts = {
  currencyCod: string,
  price: number,
  originalPrice: number,
  tax:number,
  originalTax:number,
  roe:number,
  vat: number,
  vatAmount: number,
  marginAmount:number,
  marginPercentage:number,
  originalMarginPercentage:number,
}
export type ResItemCommission = {
  baseAmount: number,
  percentage: number,
  vatPercentage: number,
  net:number,
  vat:number,
  total:number,
}
